// apiConfig.ts - Centralized API Configuration

import { getDeployEnvironment } from './deployEnv';

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

const configuredApiBaseUrl = (): string | undefined => {
  const value = process.env.REACT_APP_API_URL || process.env.REACT_APP_AUTH_API_URL;
  return value ? trimTrailingSlash(value) : undefined;
};

const configuredWebSocketUrl = (): string | undefined => {
  const explicitUrl = process.env.REACT_APP_WEBSOCKET_URL;
  if (explicitUrl) {
    return trimTrailingSlash(explicitUrl);
  }

  const authApiUrl = process.env.REACT_APP_AUTH_API_URL;
  if (!authApiUrl) {
    return undefined;
  }

  return trimTrailingSlash(authApiUrl).replace(/\/api$/i, '');
};

const currentOrigin = (): string => {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return '';
  }
  return window.location.origin;
};

// Base URL configuration
export const API_CONFIG = {
  // Base URLs for different environments
  BASE_URL: {
    local: configuredApiBaseUrl() || 'http://localhost:8888/api',
    development: configuredApiBaseUrl() || 'http://localhost:8888/api',
    production: configuredApiBaseUrl() || '/api',
    staging: configuredApiBaseUrl() || '/api'
  },

  // WebSocket URLs (direct to backend, not through /api proxy)
  WEBSOCKET_URL: {
    local: configuredWebSocketUrl() || 'http://localhost:8888',
    development: 'http://localhost:8888',
    production: configuredWebSocketUrl() || currentOrigin(),
    staging: configuredWebSocketUrl() || currentOrigin()
  },
  
  // API version (for future use)
  API_VERSION: 'v1',
  
  // Timeout configuration
  TIMEOUT: 30000, // 30 seconds

  // Multipart file-upload launches (update-deploy, update-config,
  // update-license) carry a real file over the real network, plus reverse-
  // proxy buffering overhead, before the backend ever sees the request. The
  // flat 30s timeout above was found to abort large deployment uploads on
  // staging before the request reached the backend at all (nginx buffers the
  // full multipart body to a temp file before proxying it upstream). A
  // generous ceiling here still bounds indefinite hangs - a stuck request is
  // still caught and handed to idempotency reconciliation - while giving a
  // slow real-world upload of an up-to-25MB deployment file (nginx
  // client_max_body_size) room to finish.
  UPLOAD_TIMEOUT: 300000, // 5 minutes

  // The RTU configuration/deployment file-list endpoints are the only
  // user-facing requests that synchronously block on a live DMS -> RTU network
  // round trip; every other RTU interaction is dispatched to a background
  // worker and tracked through action state, so no browser timeout applies to
  // it. The backend's own budget for that round trip is up to 30s to connect
  // (dms.rtu.http.connect-timeout-ms) plus up to 150s to read
  // (dms.rtu.http.read-timeout-ms). The flat 30s TIMEOUT above could not
  // outlast even the connect phase, so a slow-but-reachable RTU was aborted
  // client-side: the browser reported a raw "timeout of 30000ms exceeded"
  // while the backend was still mid-conversation and would have answered.
  // Confirmed on staging - the same RTU/endpoint that timed out through the UI
  // returned a full file list when the same request ran without this cap.
  // Matching the backend's full budget keeps the server the authority on the
  // outcome, so the user always sees a real diagnosis instead of a client abort.
  RTU_FILE_LIST_TIMEOUT: 180000, // 3 minutes

  // Retry configuration
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

// Get the appropriate base URL based on environment
export const getBaseUrl = (): string => {
  const deployEnvironment = getDeployEnvironment();

  if (deployEnvironment === 'local') {
    return API_CONFIG.BASE_URL.local;
  }
  if (deployEnvironment === 'development') {
    return configuredApiBaseUrl() || '/api';
  }
  if (deployEnvironment === 'production') {
    return API_CONFIG.BASE_URL.production;
  }
  return API_CONFIG.BASE_URL.staging;
};

// Get WebSocket URL (direct to backend, not through /api proxy)
export const getWebSocketUrl = (): string => {
  const deployEnvironment = getDeployEnvironment();

  if (deployEnvironment === 'local') {
    return API_CONFIG.WEBSOCKET_URL.local;
  }
  if (deployEnvironment === 'development') {
    return configuredWebSocketUrl() || currentOrigin();
  }
  if (deployEnvironment === 'production') {
    return API_CONFIG.WEBSOCKET_URL.production;
  }
  return API_CONFIG.WEBSOCKET_URL.staging;
};
// API Endpoints - Organized by feature/module
export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    VALIDATE: '/auth/validate',
  },
  
  // Actions endpoints
  ACTIONS: {
    CREATE: '/actions/create',
    START_REBOOT: '/actions/startReboot',
    START_DEPLOY: '/actions/startDeploy',
    START_CONFIG_UPDATE: '/actions/startConfigUpdate',
    LAUNCH_REBOOT: '/actions/launch/reboot',
    LAUNCH_DEPLOY: '/actions/launch/update-deploy',
    LAUNCH_CONFIG_UPDATE: '/actions/launch/update-config',
    LAUNCH_LICENSE_UPDATE: '/actions/launch/update-license',
    LAUNCH_RTU_INFO: '/actions/launch/rtu-info',
    LAUNCH_DEBUG_LOG_DOWNLOAD: '/actions/launch/download-debug-log',
    LAUNCH_CONFIG_DOWNLOAD: '/actions/launch/download-config',
    LAUNCH_DEPLOY_DOWNLOAD: '/actions/launch/download-deploy',
    DEPLOYMENT_COMPATIBILITY_PREVIEW: '/actions/deployment-compatibility/preview',
    RTU_CONFIGURATION_FILES: '/actions/rtu-files/configurations',
    RTU_DEPLOYMENT_FILES: '/actions/rtu-files/deployments',
    LAUNCH_REQUEST_STATUS: (idempotencyKey: string) => `/actions/launch/requests/${encodeURIComponent(idempotencyKey)}`,
    CANCEL: '/actions/cancel',
    CANCEL_BY_IPS: '/actions/cancelByIps',
  },
  
  // RTU endpoints
  RTU: {
    FETCH: '/fetch',
    GET_BY_ID: (id: number) => `/rtu/${id}`,
    INSTANT_SYSTEM_EVENT_HISTORY: (id: number | string) => `/rtu/${id}/instant-system-events/history`,
    PROBE_BY_ID: (rtuId: number | string) => `/rtu/${rtuId}/probe`,
    PING_RUN_BY_ID: (rtuId: number | string) => `/rtu/${rtuId}/ping-runs`,
    BULK_PING_RUN: '/rtu/ping-runs/bulk',
    PING_BATCHES: '/rtu/ping-runs/batches',
    PING_BATCH_BY_ID: (batchRunId: string) => `/rtu/ping-runs/batches/${encodeURIComponent(batchRunId)}`,
    LATEST_REACHABILITY_BY_ID: (rtuId: number | string) => `/rtu/${rtuId}/latest-reachability`,
    GENERATE_LICENSE: (rtuId: number | string) => `/rtu/${rtuId}/license`,
    STATUS_HISTORY: '/rtu/status-history',
    REACHABILITY: '/rtu/reachability',
    DELETE_ALL: '/rtu/deleteAllRtus',
    GET_ALL_DATA: '/rtu/all',
    DOWNLOAD_DB1: '/download/db1',
    ACTION_HINTS: '/rtu/action-hints',
  },

  DASHBOARD: {
    REACHABILITY_CURRENT: '/dashboard/reachability/current',
    RTUS_ADDED: '/dashboard/rtus-added',
    IMAGE_VERSIONS: '/dashboard/image-versions',
    PRODUCERS: '/dashboard/producers',
  },

  SETTINGS: {
    DASHBOARD_DEFAULTS: '/settings/dashboard-defaults',
    RETENTION: '/settings/retention',
    RETENTION_RUN: '/settings/retention/run',
    RTU_PING_TEMPLATES: '/settings/rtu-ping/templates',
    RTU_PING_ASSIGNMENTS: '/settings/rtu-ping/assignments',
    RTU_REGISTRATION_BACKFILL: '/settings/rtu-ping/registration-backfill',
  },
  
  // Notifications endpoints
  NOTIFICATIONS: {
    GET_ALL: '/notifications/all',
    MARK_AS_READ: (type: string, id: number) => `/notifications/${type}/${id}/read`,
  },
  
  // Database/Employee endpoints
  DATABASE: {
    EMPLOYEE: '/database/employee',
  },

  // Current authenticated user's backend-resolved access data
  SECURITY: {
    CURRENT_USER: '/security/current-user',
  },
  
  // Utility endpoints
  UTILITY: {
    PING: '/ping',
    TEST_BROADCAST: '/test/broadcast',
    CLEANUP: '/cleanup/trigger',
  },

  // Ingestion observability endpoints
  INGESTION: {
    HEALTH: '/ingestion/health',
    RUNTIME_METRICS: '/ingestion/metrics/runtime',
    LATENCY_METRICS: '/ingestion/metrics/latency',
    LATEST_EVENTS: '/ingestion/events/latest',
    EXECUTIVE_VIEW: '/ingestion/executive/view',
    INVESTIGATION_FILES: '/ingestion/investigation/files/latest',
    INVESTIGATION_FILES_PAGE: '/ingestion/investigation/files/page',
    INVESTIGATION_FILE_DOWNLOAD: (correlationId: string) =>
      `/ingestion/investigation/files/${encodeURIComponent(correlationId)}/download`,
    FILE_ACTIVITY_PAGE: '/ingestion/file-activity/page',
    RECEPTION_FILE_ACTIVITY_PAGE: '/ingestion/reception/file-activity/page',
    AUDIT_LATEST: '/ingestion/audit/latest',
    AUDIT_PAGE: '/ingestion/audit/page',
    QUARANTINE_LATEST: '/ingestion/quarantine/latest',
    QUARANTINE_PAGE: '/ingestion/quarantine/page',
    QUARANTINE_REDRIVE: (id: number) => `/ingestion/quarantine/${id}/redrive`,
    QUARANTINE_REDRIVE_BULK: '/ingestion/quarantine/redrive-bulk',
  },
  
  // WebSocket endpoints (for reference)
  WEBSOCKET: {
    TOPIC_BROADCAST: '/topic/broadcast',
    TOPIC_TEST: '/topic/test',
    TOPIC_PARTIAL_RTU_UPDATE: '/topic/partialRtuUpdate',
  },
} as const;

// URL Builder utility functions
export class ApiUrlBuilder {
  private baseUrl: string;
  
  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || getBaseUrl();
  }
  
  // Build full URL for an endpoint
  buildUrl(endpoint: string, params?: Record<string, string | number>): string {
    let url = `${this.baseUrl}${endpoint}`;
    
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });
      url += `?${searchParams.toString()}`;
    }
    
    return url;
  }
  
  // Build URL with path parameters
  buildUrlWithParams(endpoint: string, pathParams: Record<string, string | number>): string {
    let url = `${this.baseUrl}${endpoint}`;
    
    Object.entries(pathParams).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, String(value));
    });
    
    return url;
  }
  
  // Get base URL
  getBaseUrl(): string {
    return this.baseUrl;
  }
  
  // Set base URL
  setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl;
  }
}

// Default API URL builder instance
export const apiUrlBuilder = new ApiUrlBuilder();

// Utility functions for common URL operations
export const buildApiUrl = (endpoint: string, params?: Record<string, string | number>): string => {
  return apiUrlBuilder.buildUrl(endpoint, params);
};

export const buildApiUrlWithPathParams = (endpoint: string, pathParams: Record<string, string | number>): string => {
  return apiUrlBuilder.buildUrlWithParams(endpoint, pathParams);
};

// Environment-specific configurations
export const getApiConfig = () => ({
  baseUrl: getBaseUrl(),
  websocketUrl: getWebSocketUrl(),
  timeout: API_CONFIG.TIMEOUT,
  maxRetries: API_CONFIG.MAX_RETRIES,
  retryDelay: API_CONFIG.RETRY_DELAY,
});

// Type definitions for better TypeScript support
export type ApiEndpoint = typeof API_ENDPOINTS;
export type AuthEndpoints = typeof API_ENDPOINTS.AUTH;
export type ActionEndpoints = typeof API_ENDPOINTS.ACTIONS;
export type RtuEndpoints = typeof API_ENDPOINTS.RTU;
export type NotificationEndpoints = typeof API_ENDPOINTS.NOTIFICATIONS; 
